import { useEffect, useMemo, useRef } from "react";
import type {
	FreeArea,
	OrientationType,
	PlatformType,
} from "@rubyplay/effw-connector";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { useRegisterCommands, type Command } from "@/commands/CommandRegistryContext";
import { debugLog } from "@/utils";
import { useSubscription } from "@/hooks/useSubscription";

export type HitAreaWidgetProps = {
	name: string;

	freeAreas: readonly Readonly<FreeArea>[];
	platform: PlatformType;
	orientation: OrientationType;

	areaIds?: string[];

	visible: boolean;
	enabled: boolean;

	onActivate: () => void;

	children?: React.ReactNode;
};

export function HitAreaWidget({
	name,
	freeAreas,
	platform,
	orientation,
	areaIds,
	visible,
	enabled,
	onActivate,
	children,
}: HitAreaWidgetProps) {
	const { onFreeAreaEvent } = useCommunicator();

	const matchedAreaIds = useMemo(() => {
		const platformOrientationIds = freeAreas
			.filter((area) => area.platform === platform && area.orientation === orientation)
			.map((area) => area.id);

		const base = new Set(platformOrientationIds);

		if (!areaIds || areaIds.length === 0) {
			return base;
		}

		const filtered = new Set<string>();
		for (const areaId of areaIds) {
			if (base.has(areaId)) {
				filtered.add(areaId);
			}
		}

		return filtered;
	}, [areaIds, freeAreas, orientation, platform]);

	const activateRef = useRef(onActivate);

	useEffect(() => {
		activateRef.current = onActivate;
	}, [onActivate]);

	useSubscription(
		onFreeAreaEvent,
		(payload) => {
			if (!visible) return;
			if (!enabled) return;
			if (!matchedAreaIds.has(payload.freeAreaId)) return;

			if (
				payload.eventType === "pointerdown" ||
				payload.eventType === "mousedown" ||
				payload.eventType === "touchstart" ||
				payload.eventType === "click"
			) {
				debugLog("[HitAreaWidget] activate via FreeAreaEvent", {
					name,
					freeAreaId: payload.freeAreaId,
					eventType: payload.eventType,
					x: payload.x,
					y: payload.y,
					nx: payload.nx,
					ny: payload.ny,
				});

				activateRef.current();
			}
		},
		[enabled, matchedAreaIds, name, visible]
	);

	const handleDomPointerDownCapture = () => {
		if (!visible) return;
		if (!enabled) return;

		debugLog("[HitAreaWidget] activate via DOM pointerdown", { name });
		activateRef.current();
	};

	const commands = useMemo<Command[]>(() => {
		const cmdName = `widgets.activate.${name}`;

		return [
			{
				name: cmdName,
				execute: async () => {
					debugLog("[HitAreaWidget] activate via command", { name, cmdName });
					activateRef.current();
				},
			},
		];
	}, [name]);

	useRegisterCommands(commands);

	return (
		<div className="h-full w-full" onPointerDownCapture={handleDomPointerDownCapture}>
			<div className="h-full w-full">{children}</div>
		</div>
	);
}

export default HitAreaWidget;
